// src/contexts/SidebarContext.js
import { createContext, useContext, useState, useCallback } from 'react';

const SidebarContext = createContext();

/**
 * Tracks:
 *  • libraryOpen / utilitiesOpen  (your left/right panels)
 *  • bottomDrawerOpen / activeBottomTab  (your mobile bottom drawer)
 */
export function SidebarProvider({ children }) {
  // ←— existing left/right sidebar state
  const [libraryOpen,   setLibraryOpen]   = useState(false);
  const [utilitiesOpen, setUtilitiesOpen] = useState(false);

  // ←— NEW: track which tab is active in the *utilities* panel
  const defaultUtilitiesTab = 'scaling';
  const [activeUtilitiesTab, setActiveUtilitiesTab] = useState(defaultUtilitiesTab);


  // ←— NEW: mobile bottom-drawer state
  const [bottomDrawerOpen, setBottomDrawerOpen] = useState(false);
  const [activeBottomTab,  setActiveBottomTab]  = useState('planner');

  // ✅ NEW: per-tab badge counts (e.g. Ingredients review count)
  const [sidebarTabCounts, setSidebarTabCounts] = useState({});

  const setSidebarTabCount = useCallback((tabId, count) => {
    const nextCount = Math.max(0, Number(count) || 0);

    setSidebarTabCounts(prev => {
      const current = prev?.[tabId] ?? 0;
      if (current === nextCount) return prev; // ✅ no state update if unchanged
      return {
        ...prev,
        [tabId]: nextCount,
      };
    });
  }, []);

  // left = recipe library
  const openLibrary  = () => setLibraryOpen(true);
  const closeLibrary = () => setLibraryOpen(false);

  // right = utilities panel
  const openUtilities  = () => setUtilitiesOpen(true);
  const closeUtilities = () => setUtilitiesOpen(false);

  // bottom = mobile drawer
  const openBottomDrawer  = () => setBottomDrawerOpen(true);
  const closeBottomDrawer = () => setBottomDrawerOpen(false);

  // convenience to close all at once
  const closeSidebars = () => {
    setLibraryOpen(false);
    setUtilitiesOpen(false);
    setBottomDrawerOpen(false);
  };

  const validTabIds = ['library', 'folders', 'menus', 'versions'];
  const defaultTab = 'library';

  const [activeSidebarTab, setActiveSidebarTab] = useState(() => {
    const saved = localStorage.getItem('activeSidebarTab');
    return validTabIds.includes(saved) ? saved : defaultTab;
  });
  return (
    <SidebarContext.Provider
      value={{
        // left/right
        libraryOpen,
        openLibrary,
        closeLibrary,
  
        utilitiesOpen,
        openUtilities,
        closeUtilities,

        // ←— NEW: utilities panel active‐tab
        activeUtilitiesTab,
        setActiveUtilitiesTab,

        // ✅ NEW: badge counts for right sidebar tabs
        sidebarTabCounts,
        setSidebarTabCount,
  
        // bottom drawer
        bottomDrawerOpen,
        openBottomDrawer,
        closeBottomDrawer,
        activeBottomTab,
        setActiveBottomTab,
  
        // NEW: vertical tabs
        activeSidebarTab,
        setActiveSidebarTab,
  
        // convenience
        closeSidebars,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

// custom hook
export function useSidebar() {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error('useSidebar must be used within a SidebarProvider');
  return ctx;
}
